import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Avatar,
  Container,
  Alert,
  Fade,
  InputAdornment,
  Divider
} from '@mui/material';
import {
  Agriculture,
  Mic,
  VolumeUp,
  Email,
  Lock,
  ChevronRight
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useVoice } from '../contexts/VoiceContext';
import { useTranslation } from 'react-i18next';

const Login = () => {
  const { login, error, isLoading } = useAuth();
  const { startListening, stopListening, isListening, transcript } = useVoice();
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    email: 'demo@baserrri-aditu.eus',
    password: 'demo123'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await login(formData.email, formData.password);
    if (result && result.success) {
      window.location.href = '/';
    }
  };

  const handleVoiceLogin = () => {
    if (isListening) stopListening();
    else startListening();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'radial-gradient(circle at top left, var(--forest-main) 0%, var(--navy-main) 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      {/* Decorative Blur Elements */}
      <Box sx={{ position: 'absolute', top: -100, left: -100, width: 400, height: 400, bgcolor: 'var(--emerald-main)', opacity: 0.1, filter: 'blur(100px)', borderRadius: '50%' }} />
      <Box sx={{ position: 'absolute', bottom: -100, right: -100, width: 500, height: 500, bgcolor: 'var(--navy-main)', opacity: 0.2, filter: 'blur(120px)', borderRadius: '50%' }} />

      <Container maxWidth="xs" sx={{ position: 'relative', zIndex: 1 }}>
        <Fade in timeout={1000}>
          <Card 
            sx={{ 
              borderRadius: '32px', 
              overflow: 'hidden', 
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
              border: '1px solid rgba(255, 255, 255, 0.3)'
            }}
          >
            <CardContent sx={{ p: { xs: 4, sm: 6 } }}>
              <Box sx={{ textAlign: 'center', mb: 5 }}>
                <Avatar
                  sx={{
                    width: 72,
                    height: 72,
                    bgcolor: 'var(--forest-main)',
                    mx: 'auto',
                    mb: 2,
                    boxShadow: 'var(--shadow-lg)'
                  }}
                >
                  <Agriculture sx={{ fontSize: 36, color: 'white' }} />
                </Avatar>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 900, 
                    color: 'var(--navy-main)',
                    fontFamily: 'Montserrat',
                    letterSpacing: '-0.05em',
                  }}
                >
                  BASERRI-ADITU
                </Typography>
                <Typography variant="caption" sx={{ color: 'var(--slate-light)', mt: 1, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                  Sistema de Inteligencia Ganadera
                </Typography>
              </Box>

              {error && (
                <Alert severity="error" variant="filled" sx={{ mb: 3, borderRadius: '12px', fontWeight: 600 }}>
                  {error}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <Box sx={{ mb: 2.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: 'var(--slate-light)' }}>EMAIL PROFESIONAL</Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="usuario@dominio.eus"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Email sx={{ color: 'var(--slate-light)' }} /></InputAdornment>,
                      sx: { borderRadius: '14px', bgcolor: 'rgba(0,0,0,0.02)', mt: 0.5 }
                    }}
                    required
                  />
                </Box>
                
                <Box sx={{ mb: 4 }}>
                  <Typography variant="caption" sx={{ fontWeight: 800, ml: 1, color: 'var(--slate-light)' }}>CONTRASEÑA SEGURA</Typography>
                  <TextField
                    fullWidth
                    type="password"
                    variant="outlined"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Lock sx={{ color: 'var(--slate-light)' }} /></InputAdornment>,
                      sx: { borderRadius: '14px', bgcolor: 'rgba(0,0,0,0.02)', mt: 0.5 }
                    }}
                    required
                  />
                </Box>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={isLoading}
                  className="luxury-button luxury-button-primary"
                  sx={{
                    py: 2,
                    fontSize: '1rem',
                    boxShadow: '0 12px 24px -6px rgba(5, 150, 105, 0.4)'
                  }}
                  endIcon={<ChevronRight />}
                >
                  {isLoading ? 'Iniciando sesión...' : 'Acceder al Panel'}
                </Button>
              </form>

              <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Divider sx={{ mb: 3 }}><Typography variant="caption" sx={{ fontWeight: 800, color: 'var(--slate-light)' }}>O BIEN</Typography></Divider>
                <Button
                  variant="outlined"
                  onClick={handleVoiceLogin}
                  startIcon={isListening ? <VolumeUp /> : <Mic />}
                  sx={{
                    borderRadius: '14px',
                    py: 1.5,
                    px: 3,
                    borderColor: isListening ? 'var(--alert-crimson)' : 'var(--border-color)',
                    color: isListening ? 'var(--alert-crimson)' : 'var(--navy-main)',
                    fontWeight: 700,
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: 'var(--navy-main)',
                      bgcolor: 'rgba(0,0,0,0.02)'
                    }
                  }}
                >
                  {isListening ? 'Escuchando comandos...' : 'Acceso por Voz'}
                </Button>
                
                {isListening && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'var(--alert-crimson)', fontWeight: 700 }}>
                    "{transcript || 'Diga "entrar" o su nombre...'}"
                  </Typography>
                )}
              </Box>

              <Box sx={{ mt: 5, textAlign: 'center', p: 2, bgcolor: 'var(--slate-bg)', borderRadius: '16px' }}>
                <Typography variant="caption" sx={{ color: 'var(--slate-light)', fontWeight: 600, display: 'block' }}>
                  Demo Authorized Access:
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 800, color: 'var(--navy-main)' }}>
                  demo@baserrri-aditu.eus / demo123
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Fade>
      </Container>
    </Box>
  );
};

export default Login;
